/**
 * 回忆页的“最近一趟旅行”（TRV-06，2026-09-26）：只收真事件。
 * - 数据只用两条现有读接口（主窗口转 I 定：按宠物列历次计划、回忆的读接口 A、I 还没给，给之前不拿别的接口去拼）：
 *   GET /travel/wish（进行中的心愿优先，没有就给最近一条）→ 它已发布的计划（publishedPlanId）→ GET /travel/plans/{plan_id}。
 *   读法和查询键与攻略页（journey/travelPlan 的 useCurrentWish、useLivePlan）是同一组：从回忆页点进那份计划再回来、刷新，两边是同一份数据；
 *   切宠物时键跟着换（键里有账号和宠物），不会把上一只的回忆留在页面上。
 * - 只在这份计划当前那一版里有“回忆手账”（phase = memory：TA 回到家时后端按真实到访写的那一页）时才出现。
 *   只有计划手账（还没出门、还在路上）、没有心愿、心愿还没有计划、读不到（没权限、不是这只宠物的计划、别的错误）都不出现，
 *   也不写错误文字——回忆页的惯例：读不到只隐藏这一块，别的入口照常。
 * - 盖章与说法全用攻略页的读模型（./model 的 PlanView）：到访章只认回忆手账站点上的真实到访事件（visited_event_ids），
 *   没去成的写“下次”；心愿与计划对不上（以前的心愿）就只按计划本身说（viewOfPlanOnly），同攻略页。
 * - 演示模式用演示计划里带回忆手账的那几份中最晚写的一份，处处挂“演示”；演示数据只在演示模式出现，不进 live。
 * 已知缺口（已报主窗口，等 A、I）：新心愿一形成，上一趟的回忆就只能从攻略页的旧链接进，这一块读不到。
 */
import type { TravelJournal, TravelPlan, TravelWish } from "@/shared/contracts";
import { env } from "@/shared/config/env";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { useCurrentWish, useDemoPlans, useLivePlan } from "@/features/journey/travelPlan/data";
import { publishedPlanOf, viewOfPlan, viewOfPlanOnly, type PlanBundle, type PlanView, type ViewOptions } from "@/features/journey/travelPlan/model";

/** 这一块的读法：还在读、没有可放的、读到了。读不到（出错）也算“没有可放的”，页面只是不显示。 */
export type TripMemoryRead = { state: "loading" } | { state: "none" } | { state: "ready"; view: PlanView };

/**
 * 这条心愿已经发布过的计划：直接转调攻略页的 publishedPlanOf（travelPlan/model：plan_id 和 plan_revision 都有才算，和后端同一口径），
 * 只把“没有”从 null 换成 undefined（useLivePlan 收的是 string | undefined）。口径只在 model 写一份，两边不会走样（2026-09-27，DS5 加了 publishedPlanOf）。
 * 不能只看 plan_id：后端在心愿形成时就给了 plan_id，计划还没写好时拿它去读只会得到 404 plan_not_found。
 * 计划和它那一页计划手账是同一次写下的，所以有已发布的计划就有计划手账（回忆页“旅程与攻略手账”那一句据此数，见 ./MemoriesPage）。
 */
export function publishedPlanId(wish: TravelWish | null | undefined): string | undefined {
  return publishedPlanOf(wish) ?? undefined;
}

/**
 * 这份计划当前那一版（current_revision）的回忆手账：同一版里取 journal_revision 最大的一页；没有就是 null。
 * 挂在这一版修订上、plan_revision 却不同的不认（手账按理挂在自己那一版上，这里再守一次）。
 */
export function memoryJournalOf(plan: TravelPlan): TravelJournal | null {
  const revision = plan.revisions.find((r) => r.plan_revision === plan.current_revision);
  if (!revision) return null;
  let best: TravelJournal | null = null;
  for (const journal of revision.journals) {
    if (journal.phase !== "memory" || journal.plan_revision !== revision.plan_revision) continue;
    if (!best || journal.journal_revision > best.journal_revision) best = journal;
  }
  return best;
}

const writtenAt = (journal: TravelJournal): number => {
  const at = Date.parse(journal.created_at);
  return Number.isNaN(at) ? -Infinity : at;
};

/** 演示计划里带回忆手账的几份中，回忆手账写得最晚的一份；一份都没有就是 null。 */
export function latestDemoMemory(bundles: readonly PlanBundle[]): PlanBundle | null {
  let best: { bundle: PlanBundle; at: number } | null = null;
  for (const bundle of bundles) {
    const journal = memoryJournalOf(bundle.plan);
    if (!journal) continue;
    const at = writtenAt(journal);
    if (!best || at > best.at) best = { bundle, at };
  }
  return best?.bundle ?? null;
}

/**
 * 当前宠物最近一趟旅行的回忆（见文件头）。宠物名字的取法与攻略页相同（家庭上下文里当前宠物的名字，拿不到写“TA”），
 * 所以这一块的标题和点进去那一页的标题一字不差。
 */
export function useLatestTripMemory(): TripMemoryRead {
  const fixture = env.dataMode === "fixture";
  const petName = useOptionalCurrentHousehold()?.pet?.name ?? null;
  const wish = useCurrentWish();
  const demo = useDemoPlans();
  const planId = !fixture && wish.state === "ready" ? publishedPlanId(wish.data) : undefined;
  const plan = useLivePlan(planId);
  const options: ViewOptions = { petName, isDemo: fixture };

  if (fixture) {
    if (demo.state === "loading") return { state: "loading" };
    if (demo.state !== "ready") return { state: "none" };
    const bundle = latestDemoMemory(demo.data);
    return bundle ? { state: "ready", view: viewOfPlan(bundle, options) } : { state: "none" };
  }
  if (wish.state === "loading") return { state: "loading" };
  if (wish.state !== "ready" || !wish.data || !planId) return { state: "none" };
  if (plan.state === "loading") return { state: "loading" };
  if (plan.state !== "ready" || !memoryJournalOf(plan.data)) return { state: "none" };
  const current = wish.data.wish_id === plan.data.wish_id ? wish.data : null;
  return { state: "ready", view: current ? viewOfPlan({ wish: current, plan: plan.data }, options) : viewOfPlanOnly(plan.data, options) };
}
